import { Square } from 'lucide-react';
import { Button, ProgressBar, cn } from '@ai-tutor/ui';
import { BreatheCircle } from './BreatheCircle';
import { BreatheDone } from './BreatheDone';
import { BreatheSetup } from './BreatheSetup';
import { BREATHING_PATTERNS, REST_SCALE, breathsIn } from './breathingPatterns';
import type {
  BreatheFeeling,
  BreathePrefs,
  BreatheStatus,
} from './breatheTypes';

export interface BreatheRun {
  /** The current instruction, and its place in the pattern. */
  cue: string;
  stepIndex: number;
  /** Size this breath eases to, and how long it takes. */
  scale: number;
  stepSeconds: number;
  /** 0–1 through this breath; drives the reduced-motion bar. */
  stepProgress: number;
  /** Whole seconds left in this breath. */
  countdown: number;
  breath: { current: number; total: number };
  /** 0–1 through the whole session. */
  sessionProgress: number;
  remainingLabel: string;
}

export interface BreatheStageViewProps {
  status: BreatheStatus;
  prefs: BreathePrefs;
  /** While running: where in the session it is. */
  run?: BreatheRun;
  feeling: BreatheFeeling | null;
  reducedMotion?: boolean;
  onPrefsChange: (prefs: BreathePrefs) => void;
  onStart: () => void;
  onStop: () => void;
  onFeeling: (feeling: BreatheFeeling) => void;
}

function RunningCaption({
  run,
  reducedMotion,
  onStop,
}: {
  run: BreatheRun;
  reducedMotion: boolean;
  onStop: () => void;
}) {
  return (
    <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
      <div aria-live='polite'>
        <p className='text-2xl font-semibold text-foreground'>{run.cue}</p>
      </div>
      <p className='font-mono text-xs tabular-nums text-muted-foreground'>
        Breath {run.breath.current} of {run.breath.total} · {run.remainingLabel}{' '}
        left
      </p>
      {reducedMotion && (
        <ProgressBar
          value={Math.round(run.stepProgress * 100)}
          size='sm'
          ariaLabel='Progress through this breath'
          className='w-full'
        />
      )}
      <Button className='max-sm:h-11' variant='outline' onClick={onStop}>
        <Square className='mr-2 h-4 w-4' aria-hidden='true' />
        Stop
      </Button>
    </div>
  );
}

/** The circle at rest (breathing gently), running (this breath) or done (still, ring full). */
function circleFor(status: BreatheStatus, run: BreatheRun | undefined) {
  if (status === 'running' && run) {
    return {
      scale: run.scale,
      stepSeconds: run.stepSeconds,
      sessionProgress: run.sessionProgress,
      countdown: run.countdown,
    };
  }
  return {
    scale: REST_SCALE,
    stepSeconds: 0.6,
    sessionProgress: status === 'done' ? 1 : 0,
    resting: status === 'idle',
    settled: status === 'done',
  };
}

/**
 * The Breathe page's stage, drawn from props: `BreatheRoom` owns the clock.
 * One calm card. At rest the circle sits beside the choices; running, the
 * choices go and the circle has the card to itself; afterwards, the circle
 * rests beside "How do you feel now?".
 */
export function BreatheStageView({
  status,
  prefs,
  run,
  feeling,
  reducedMotion = false,
  onPrefsChange,
  onStart,
  onStop,
  onFeeling,
}: BreatheStageViewProps) {
  const running = status === 'running' && run !== undefined;
  return (
    <section
      aria-label='Breathing exercise'
      className='rounded-2xl bg-card/70 p-6 shadow-elevation-1 backdrop-blur-sm dark:bg-card sm:p-10'
    >
      <div
        className={cn(
          'grid items-center gap-8 lg:gap-12',
          !running && 'lg:grid-cols-2'
        )}
      >
        {/* At rest on a phone the preview circle is left out, so the
          choices and "Start breathing" fit the first screen; the circle
          takes the stage the moment a session starts. */}
        <div
          className={cn(
            'flex-col items-center gap-4 sm:gap-6',
            status === 'idle' ? 'hidden sm:flex' : 'flex'
          )}
        >
          <BreatheCircle
            {...circleFor(status, run)}
            reducedMotion={reducedMotion}
          />
          {status === 'idle' && (
            // What the circle is previewing, so it is more than decoration.
            <p className='text-sm text-muted-foreground'>
              {BREATHING_PATTERNS[prefs.pattern].label} · {prefs.length} min
            </p>
          )}
          {running && (
            <RunningCaption
              run={run}
              reducedMotion={reducedMotion}
              onStop={onStop}
            />
          )}
        </div>
        {status === 'idle' && (
          <BreatheSetup
            prefs={prefs}
            onChange={onPrefsChange}
            onStart={onStart}
          />
        )}
        {status === 'done' && (
          <BreatheDone
            summary={{
              breaths: breathsIn(
                BREATHING_PATTERNS[prefs.pattern],
                Number(prefs.length) * 60_000
              ),
              minutes: Number(prefs.length),
            }}
            feeling={feeling}
            onFeeling={onFeeling}
            onAgain={onStart}
          />
        )}
      </div>
    </section>
  );
}
